// Pure derivations for forecast COPY links. No React, no fetch, no DOM
// (lib/ convention). Every function here is a total function of its arguments.
//
// URL-fragment handling — reading the link secret out of the fragment and
// clearing it — lives in the sibling ./url.js, because rewriting the address bar
// is a view-layer effect rather than a derivation.
//
// Wording rule: this is the COPY verb, and the string "share" must never appear
// in anything this module produces. Copying gives the recipient THEIR OWN
// forecast; the owner cannot edit it, revoke it, or enumerate who holds it.
// Revocation stops NEW redemptions only and never reaches a copy that already
// exists — so no label here may imply recall (design §2, capsule
// docs/capsules/forecast-copy-links.md).
//
// LIVENESS IS THE SERVER'S ANSWER, NOT OURS. `listCopyLinks` derives each row's
// `state` through the module-private `isLive()` (server/handlers/copyLinks.js:68,
// used at :1302) and that predicate fails CLOSED on an unparseable `expires_at`.
// We deliberately do NOT recompute it, because we cannot: pg parses
// `'infinity'::timestamptz` to a non-finite Date, `isLive` reads that as expired,
// and the handler passes the value through unnormalised (:1295-1300 — normalising
// would throw RangeError), so JSON.stringify hands the browser `null`. A
// client-side recomputation would call that link live and contradict what redeem
// does. So `state` decides; `expiresAt` is display material only, and every
// derivation below treats a state it does not recognise as "not a live
// capability".
//
// No reuse of lib/formatDates.js: `fmtRelative` there formats a PAST instant
// ("3d ago") and reads Date.now() internally, so it can neither express remaining
// life nor be driven from an injected clock.

const DAY_MS = 86400000;

/**
 * Mint's expiry window. Hand-kept mirrors of `DEFAULT_EXPIRY_DAYS` /
 * `MAX_EXPIRY_DAYS` in server/handlers/copyLinks.js:30-31 — the frontend bundle
 * cannot import the server's CJS, so this is the same
 * kept-in-lockstep-by-hand duplication the capsule documents for
 * `isLive`/`isLinkLive` and the message-role lists. If those constants move,
 * move these.
 */
export const DEFAULT_EXPIRY_DAYS = 90;
export const MAX_EXPIRY_DAYS = 365;

/**
 * Would mint accept this `expiryDays` body value? A literal mirror of the guard
 * at server/handlers/copyLinks.js:168-169, including its two quirks: `null` /
 * `undefined` mean "use the default" (valid), and a numeric string or a
 * fractional day is accepted because the server only checks
 * finite-and-in-range after `Number()`.
 *
 * @param {unknown} value  the value a caller would send as `expiryDays`
 * @returns {boolean} true if mint would not answer 400 for it
 */
export function isValidExpiryDays(value) {
  const days = value == null ? DEFAULT_EXPIRY_DAYS : Number(value);
  return Number.isFinite(days) && days > 0 && days <= MAX_EXPIRY_DAYS;
}

// A Map, not an object literal: an object lookup answers for inherited keys, so
// a row carrying `state: "toString"` would yield a function as a label.
const STATE_LABELS = new Map([
  ["live", "Active"],
  ["revoked", "Revoked"],
  ["expired", "Expired"],
]);

/**
 * The badge text for one link's server-derived state.
 *
 * An unrecognised, missing or wrongly-cased state answers "Unknown" rather than
 * falling through to "Active": the fail-open direction here would tell an
 * operator a capability is live when nothing said so.
 *
 * @param {{state?: string}|null|undefined} link  one `GET /api/copy-links` row
 * @returns {"Active"|"Revoked"|"Expired"|"Unknown"}
 */
export function linkStateLabel(link) {
  return STATE_LABELS.get(link?.state) ?? "Unknown";
}

/**
 * Parse a wire `expiresAt` into epoch ms, or NaN when it cannot be trusted.
 *
 * Accepts the three forms a caller can hold — the ISO string the API emits, an
 * epoch number, and a `Date` — and rejects everything else BY TYPE rather than
 * letting `new Date()` coerce it: `new Date(null)` is the epoch, so a `null`
 * expiry (reachable, see the header) would otherwise read as a link that
 * expired in 1970.
 *
 * @param {unknown} expiresAt
 * @returns {number} epoch ms, or NaN
 */
function expiryMs(expiresAt) {
  if (expiresAt instanceof Date) return expiresAt.getTime();
  // "" needs no special case: `new Date("")` is already an Invalid Date, so it
  // lands on NaN through the same path as "not-a-date" (mutation-verified — an
  // explicit `expiresAt !== ""` guard here was unkillable dead code).
  if (typeof expiresAt === "string" || typeof expiresAt === "number") {
    return new Date(expiresAt).getTime();
  }
  return Number.NaN;
}

/**
 * Human text for a link's remaining life.
 *
 * Three rules, all about not overstating a capability:
 *  - a non-live state never shows a countdown (a revoked link's expiry is
 *    irrelevant, and showing one implies it is still redeemable);
 *  - days are FLOORED, not rounded — rounding 1d22h up to "2 days" claims a day
 *    the link does not have;
 *  - an expiry we cannot parse says so, instead of computing from a coerced
 *    value or emitting "NaN days".
 *
 * A live link whose instant has already passed reads "Expired". That is
 * reachable without corruption (it expired between the response and the render,
 * or the client clock leads the server's) and understating is the safe side.
 *
 * @param {{state?: string, expiresAt?: unknown}|null|undefined} link
 * @param {number} [now]  epoch ms; injected so this stays clock-free
 * @returns {string}
 */
export function expiryText(link, now = Date.now()) {
  const state = link?.state;
  if (state === "revoked") return "Revoked";
  if (state === "expired") return "Expired";
  if (state !== "live") return "Unknown";
  const t = expiryMs(link.expiresAt);
  if (Number.isNaN(t) || !Number.isFinite(now)) return "Expiry unknown";
  const remaining = t - now;
  if (remaining <= 0) return "Expired";
  if (remaining < DAY_MS) return "Expires in less than a day";
  const days = Math.floor(remaining / DAY_MS);
  return `Expires in ${days} ${days === 1 ? "day" : "days"}`;
}
